export const APP_PLATFORMS = ['ios', 'android'] as const;
export type AppPlatform = typeof APP_PLATFORMS[number];

export const APP_VERSION_PATTERN = /^\d{1,4}(\.\d{1,4}){0,3}$/;
export const APP_VERSION_MESSAGE_MAX_LENGTH = 280;
export const UPDATE_BANNER_SNOOZE_MS = 24 * 60 * 60 * 1000;

export function isAppPlatform(value: unknown): value is AppPlatform {
  return typeof value === 'string' && (APP_PLATFORMS as readonly string[]).includes(value);
}

// Some Android builds report the build fingerprint, e.g.
// "samsung/a56xnaeea/a56x:16/BP4A.251205.006/...:user/release-keys".
const ANDROID_BUILD_FINGERPRINT_PATTERN = /^[^/\s]+\/[^/\s]+\/[^/\s]+:\d+.*\/(release|dev|test)-keys$/i;

/** Maps device-reported OS names ("Android", "iOS", "iPadOS", Android fingerprints) to a platform. */
export function appPlatformFromOsName(osName: string | null | undefined): AppPlatform | null {
  const normalized = (osName ?? '').trim().toLowerCase();
  if (normalized === 'android' || ANDROID_BUILD_FINGERPRINT_PATTERN.test(normalized)) return 'android';
  if (normalized === 'ios' || normalized === 'ipados' || normalized === 'iphone os') return 'ios';
  return null;
}

function parseVersion(value: string | null | undefined): number[] | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!APP_VERSION_PATTERN.test(trimmed)) return null;
  return trimmed.split('.').map(Number);
}

export function isValidAppVersion(value: unknown): value is string {
  return typeof value === 'string' && parseVersion(value) !== null;
}

/** Returns -1, 0 or 1, or null when either version cannot be parsed. */
export function compareAppVersions(left: string | null | undefined, right: string | null | undefined): -1 | 0 | 1 | null {
  const a = parseVersion(left);
  const b = parseVersion(right);
  if (!a || !b) return null;
  const length = Math.max(a.length, b.length);
  for (let index = 0; index < length; index += 1) {
    const difference = (a[index] ?? 0) - (b[index] ?? 0);
    if (difference < 0) return -1;
    if (difference > 0) return 1;
  }
  return 0;
}

/** True only when both versions parse and current is older than recommended. */
export function isAppVersionBehind(current: string | null | undefined, recommended: string | null | undefined): boolean {
  return compareAppVersions(current, recommended) === -1;
}

export interface UpdateBannerInput {
  currentVersion: string | null | undefined;
  recommendedVersion: string | null | undefined;
  dismissedAtMs: number | null;
  nowMs: number;
}

/** The banner shows when the app is behind and was not dismissed in the last day. */
export function shouldShowUpdateBanner(input: UpdateBannerInput): boolean {
  if (!isAppVersionBehind(input.currentVersion, input.recommendedVersion)) return false;
  if (input.dismissedAtMs === null) return true;
  return input.nowMs - input.dismissedAtMs >= UPDATE_BANNER_SNOOZE_MS;
}
